// Configuración → Funciones avanzadas → las dos tuercas del Agente: con qué MOTOR
// genera sus imágenes y qué HERRAMIENTAS puede usar al responder.
//
// Vivían como pastillas en la caja del Agente de la home, en la posición "Agente" del
// switch (ADR-2026-09-22-el-switch-arranca-en-agente). Se mudaron acá enteras —de la
// caja se fueron— por ADR-2026-09-24-el-motor-y-las-herramientas-viven-en-configuracion.
//
// NO duplican estado: leen y escriben el MISMO `ChatAtlasContext` que leía la caja
// (`modeloDeImagen`/`setModeloDeImagen` e `interruptores`/`setInterruptor`), que es
// quien persiste en `localStorage` y a quien le pregunta el chat en cada turno. Por eso
// se puede tener el panel del chat abierto al lado y ver el cambio sin recargar: es una
// sola fuente, no dos que se sincronizan. `ChatAtlasRaiz` envuelve TODAS las rutas en
// `App.tsx`, así que `/configuracion` está adentro del provider como cualquier otra.
//
// Las dos son del AGENTE, no de la empresa: el motor sólo rige para las imágenes que
// genera el chat (lo lee `ChatPanelV2` por `ProveedorDeImagenContext`) y los
// interruptores son lo que el agente tiene permitido usar al contestar. De ahí que
// lleven el mismo piso de plan que "Atlas siempre a mano" (`puedeUsarElAgente`,
// ADR-2026-09-14-el-agente-es-de-pro-y-ultra) y que se escondan —no que se muestren con
// candado— cuando el plan no llega: es el criterio que ya usa `FuncionesAvanzadas`.
import { useId, useState } from 'react';
import { ChevronDown, Cpu, Wrench } from 'lucide-react';
import { useChatAtlas } from '../../chat-atlas';
import { OPCIONES_DE_MOTOR, type ProveedorDeImagen } from '../../lib/proveedoresDeImagen';
import { INTERRUPTORES_MENU_MAS } from '../../chat-atlas/ui/menuMasItems';
import { puedeUsarElAgente } from '../../lib/planCapabilities';
import { Interruptor } from './Interruptor';

/** El chip de ícono de la card, con el mismo realce que usa `FuncionesAvanzadas`:
 *  encendido cuando el usuario TOCÓ algo, apagado cuando está como viene de fábrica. */
function ChipDeIcono({ activo, children }: { activo: boolean; children: React.ReactNode }) {
  return (
    <span
      className={`shrink-0 grid place-items-center w-10 h-10 rounded-xl ${
        activo ? 'text-brand-primary' : 'bg-neutral-100 text-neutral-400'
      }`}
      // Inline por lo mismo que en `FuncionesAvanzadas`: `bg-brand-primary/10` no compila.
      style={activo ? { backgroundColor: 'rgba(22, 68, 235, 0.10)' } : undefined}
    >
      {children}
    </span>
  );
}

/**
 * La card de una tuerca: cerrada muestra qué es y EN QUÉ QUEDÓ; abierta, las opciones.
 * Las dos arrancan CERRADAS (ADR-2026-09-24-las-dos-tuercas-del-agente-arrancan-plegadas).
 *
 * El encabezado entero es el botón —no un chevron chiquito al costado—: es lo que se
 * toca, así que es lo que tiene que responder al toque. El `valor` de la derecha es lo que
 * antes decía la pastilla cerrada de la caja del Agente, y es justamente lo que hace que
 * plegarlas no esconda nada: sin abrir se sabe con qué motor genera y cuántas
 * herramientas están prendidas.
 *
 * Plegado en el cliente y nada más: no se persiste cuál quedó abierta. Es una pantalla de
 * configuración, se entra a cambiar una cosa y se sale; recordar el estado sería recordar
 * que una vez la abriste.
 */
function CardPlegable({
  icono,
  activo,
  titulo,
  bajada,
  valor,
  children,
}: {
  icono: React.ReactNode;
  /** Prende el chip del ícono: el usuario tocó algo, no está como viene de fábrica. */
  activo: boolean;
  titulo: string;
  bajada: string;
  /** Lo que se ve cerrada: el motor elegido, o cuántas herramientas hay prendidas. */
  valor: string;
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(false);
  const idCuerpo = useId();

  return (
    <div className="rounded-2xl border border-neutral-200">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-expanded={abierta}
        aria-controls={idCuerpo}
        className="flex w-full items-start gap-4 rounded-2xl py-4 px-5 text-left transition-colors hover:bg-neutral-50"
      >
        <ChipDeIcono activo={activo}>{icono}</ChipDeIcono>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-brand-black">{titulo}</p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-500">{bajada}</p>
          {/* En qué quedó, DEBAJO de la bajada y no arriba a la derecha: leído de corrido
              —qué es esto, qué hace, en qué está— en vez de un dato suelto en la otra
              punta de la card. Sólo cerrada: abierta lo dice la opción marcada. */}
          {!abierta && (
            <p className="mt-2 text-sm font-medium text-brand-primary">{valor}</p>
          )}
        </div>
        <ChevronDown
          className={`mt-0.5 w-4 h-4 shrink-0 text-neutral-400 transition-transform ${
            abierta ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {/* Sangrado hasta donde arranca el texto (ícono 40px + gap 16px = pl-14): las
          opciones se leen como parte de esta función y no como una lista suelta. Es el
          mismo sangrado que usan los "modos" de una función en `FuncionesAvanzadas`. */}
      {abierta && (
        <div id={idCuerpo} className="px-5 pb-4">
          <div className="border-t border-neutral-200 pt-4 sm:pl-14">{children}</div>
        </div>
      )}
    </div>
  );
}

/**
 * El motor de imagen del Agente. Elección única entre las opciones que el Agente OFRECE
 * (`OPCIONES_DE_MOTOR`, con Atlas —`id: null`, "elegimos nosotros"— a la cabeza).
 *
 * Es un `radiogroup` de verdad y no una lista de botones: son opciones excluyentes, y así
 * las flechas del teclado recorren el grupo y el lector de pantalla dice "1 de 5". Cada
 * opción necesita su bajada —qué motor conviene para qué— así que va en filas y no en
 * chips, igual que cuando era una pastilla.
 */
function MotorDeImagen() {
  const { modeloDeImagen, setModeloDeImagen } = useChatAtlas();
  const elegido = OPCIONES_DE_MOTOR.find((o) => o.id === modeloDeImagen) ?? OPCIONES_DE_MOTOR[0];

  return (
    <CardPlegable
      icono={<Cpu className="w-5 h-5" />}
      activo={modeloDeImagen !== null}
      titulo="Motor de imagen"
      bajada="Con qué motor de IA genera sus imágenes el Agente. Atlas elige el mejor para cada caso. Es tuyo y de este navegador, no de la empresa."
      // Cerrada, el nombre del motor elegido es TODO lo que la card dice de su estado.
      valor={elegido.label}
    >
      <div role="radiogroup" aria-label="Motor de imagen" className="space-y-1">
        {OPCIONES_DE_MOTOR.map((o) => {
          const activo = o.id === modeloDeImagen;
          return (
            <button
              key={o.id ?? 'auto'}
              type="button"
              role="radio"
              aria-checked={activo}
              onClick={() => setModeloDeImagen(o.id as ProveedorDeImagen | null)}
              className={`flex w-full items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                activo ? 'bg-[#F2F5FE]' : 'hover:bg-neutral-50'
              }`}
            >
              {/* El radio dibujado a mano: la marca no es SÓLO el fondo, que sobre un
                  fondo tan tenue no alcanza para decir cuál está elegida. */}
              <span
                className={`mt-0.5 shrink-0 grid place-items-center w-4 h-4 rounded-full border-2 transition-colors ${
                  activo ? 'border-brand-primary' : 'border-neutral-300'
                }`}
              >
                {activo && <span className="w-2 h-2 rounded-full bg-brand-primary" />}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block text-sm font-medium ${
                    activo ? 'text-brand-primary' : 'text-brand-black'
                  }`}
                >
                  {o.label}
                </span>
                <span className="mt-0.5 block text-sm leading-relaxed text-neutral-500">
                  {o.detalle}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </CardPlegable>
  );
}

/**
 * Las herramientas del Agente: los tres interruptores sueltos (Investigación, Búsqueda
 * web, Respuestas gráficas), los tres prendidos de fábrica.
 *
 * Salen del MISMO módulo que los listaba en la caja y antes en el menú del "+"
 * (`INTERRUPTORES_MENU_MAS`): cambió dónde se prenden, no cuáles son ni qué dice cada uno.
 */
function HerramientasDelAgente() {
  const { interruptores, setInterruptor } = useChatAtlas();
  const prendidas = INTERRUPTORES_MENU_MAS.filter((i) => interruptores[i.id]).length;

  return (
    <CardPlegable
      icono={<Wrench className="w-5 h-5" />}
      // SIEMPRE gris, como el resto de los chips de la pantalla. Esta card no es un
      // interruptor sino un grupo de tres, así que su ícono no tiene un "encendido" que
      // representar: con `prendidas > 0` quedaba azul de fábrica —las tres vienen
      // prendidas— y era el único ícono encendido de toda la pantalla sin que el usuario
      // hubiera tocado nada. Lo que hay adentro lo cuenta el valor ("3 activas"), no el
      // color del ícono.
      activo={false}
      titulo="Herramientas del Agente"
      bajada="Lo que el Agente puede usar al responder. Quedan como las dejes. Son tuyas y de este navegador, no de la empresa."
      // El mismo recuento que mostraba la pastilla cerrada. Importa que se vea sin abrir
      // nada porque Investigación gasta plata de verdad (scrapers).
      valor={
        prendidas === 0 ? 'Ninguna' : `${prendidas} ${prendidas === 1 ? 'activa' : 'activas'}`
      }
    >
      <div className="space-y-4">
        {INTERRUPTORES_MENU_MAS.map((i) => (
          <div key={i.id} className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-brand-black">{i.etiqueta}</p>
              <p className="mt-1 text-sm leading-relaxed text-neutral-500">{i.ayuda}</p>
            </div>
            <Interruptor
              activo={interruptores[i.id]}
              etiqueta={i.etiqueta}
              onChange={(v) => setInterruptor(i.id, v)}
            />
          </div>
        ))}
      </div>
    </CardPlegable>
  );
}

/**
 * Las dos cards juntas. Se montan desde `FuncionesAvanzadas` y se esconden enteras cuando
 * el plan no llega al Agente: sin él, ni el motor ni las herramientas rigen sobre nada.
 */
export function OpcionesDelAgente({ plan }: { plan?: string | null }) {
  if (!puedeUsarElAgente(plan)) return null;
  return (
    <>
      <MotorDeImagen />
      <HerramientasDelAgente />
    </>
  );
}
